import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { AlertTriangle, CheckCircle2, Crosshair, MapPin, Minus, Plus, Trash2, X } from 'lucide-react';
import type { PlanCoordinates, ProjectPlan, ProjektVerortung, VerortungPlan } from '../../types';
import {
    gpsZustandAnzeige, standortVerfuegbar, useStandortSensoren, type EmpfangenePosition,
} from '../../services/standortSensoren';
import {
    geschossNameNormalisieren, gpsMitteln, planGeoreferenz, useVerortungGeraet, utmFormatieren, utmZoneFuer, wgs84ZuUtm,
    type Georeferenz,
} from '../../services/verortung';
import { registriereZurueck } from '../../services/appUmfang';
import {
    achseLoeschen, achsenUebertragen, achseSetzen, georeferenzAnzeige, gpsFixeAb, istGeoreferenz, passpunktHinzufuegen,
    passpunktLoeschen, punktAusKlick, seitenverhaeltnisNeu, standardPasspunktName, utmFuerPlanLesen, zahlText, zoneFuerPlan,
} from './einrichtungLogik';

/**
 * Vollbild-Planansicht zum Kalibrieren eines Plans (docs/verortung-kamera-vertrag.md,
 * Paket C; Plan 9.2): Passpunkte (Planstelle ↔ UTM, per Eingabe oder GPS 30 s)
 * und benannte Achskreuze antippen; Georeferenz über gleichnamige Achsen von
 * einem anderen Plan übernehmen.
 *
 * Jede Änderung geht sofort über `onChange` (ganze Kalibrierung des Plans,
 * unveränderlich kopiert); ohne `onChange` nur Ansicht. Sensoren laufen nur
 * während der GPS-Messung (Datenschutz, Plan Abschnitt 1). Kein Browser-Dialog.
 */

const GPS_DAUER_MS = 30_000;
const ZOOM_STUFEN = [1, 1.5, 2, 3, 4] as const;
const QUELLE_TEXT: Record<'gps' | 'utm' | 'achse', string> = { gps: 'GPS', utm: 'UTM', achse: 'Achse' };

type Modus = 'passpunkt' | 'achse';

interface Meldung {
    text: string;
    ton: 'ok' | 'fehler';
}

interface Messung {
    start: number;
    punkt: PlanCoordinates;
    name: string;
}

/** Werte, die Zeitgeber- und Tasten-Handler zum Zeitpunkt des Auslösens brauchen. */
interface Aktuell {
    positionen: EmpfangenePosition[];
    kalibrierung: VerortungPlan | undefined;
    onChange: ((k: VerortungPlan) => void) | undefined;
    /** GPS-Messung noch erlaubt (Gerät + Projekt an)? Beim Abschalten während der Messung → Abbruch. */
    gpsErlaubt: boolean;
}

// ─── Kleine Bausteine ────────────────────────────────────────────────────────

/** Stand der Georeferenz eines Plans (auch in der Einrichtungskarte genutzt). */
export function GeoreferenzStatus({ ergebnis, passpunkte }: {
    ergebnis: Georeferenz | { fehler: string } | null;
    passpunkte: number;
}): JSX.Element {
    if (ergebnis === null) {
        return (
            <p className="text-xs text-slate-500">
                nicht georeferenziert
                {passpunkte < 2
                    ? ` · ${passpunkte} von mind. 2 Passpunkten`
                    : ' · Planbild einmal unter „Kalibrieren“ öffnen (Seitenverhältnis fehlt)'}
            </p>
        );
    }
    if (!istGeoreferenz(ergebnis)) {
        return (
            <p className="text-xs text-rose-700 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{ergebnis.fehler}</span>
            </p>
        );
    }
    const a = georeferenzAnzeige(ergebnis);
    return (
        <div className="text-xs text-slate-700">
            <p className="flex items-start gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                <span>
                    Georeferenziert: Maßstab {a.massstab} · Drehung {a.drehung} · Restfehler {a.restfehler ?? 'ab 3 Passpunkten'}
                </span>
            </p>
            {ergebnis.hinweise.map((h) => (
                <p key={h} className="mt-1 rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1">{h}</p>
            ))}
        </div>
    );
}

function Marker({ punkt, farbe, name, pulsieren }: {
    punkt: PlanCoordinates;
    farbe: string;
    name?: string;
    pulsieren?: boolean;
}): JSX.Element {
    return (
        <div className="absolute pointer-events-none" style={{ left: `${punkt.x}%`, top: `${punkt.y}%` }}>
            <span
                className={`absolute block w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white shadow ${farbe} ${pulsieren ? 'animate-pulse' : ''}`}
            />
            {name && (
                <span className={`absolute left-2.5 -top-5 whitespace-nowrap rounded px-1 text-[11px] font-semibold text-white shadow ${farbe}`}>
                    {name}
                </span>
            )}
        </div>
    );
}

// ─── Ansicht ─────────────────────────────────────────────────────────────────

export interface PlanKalibrierungProps {
    plan: ProjectPlan;
    /** Projekt-Verortung (Kalibrierung dieses und der anderen Pläne). */
    verortung: ProjektVerortung;
    /** Alle Pläne des Projekts — Quelle für „Von Plan … übernehmen“. */
    plaene: readonly ProjectPlan[];
    /** Neue Kalibrierung dieses Plans. Fehlt → nur Ansicht. */
    onChange?: (k: VerortungPlan) => void;
    onSchliessen: () => void;
}

export function PlanKalibrierung({ plan, verortung, plaene, onChange, onSchliessen }: PlanKalibrierungProps): JSX.Element {
    const kalibrierung = verortung.plaene?.[plan.id];
    const passpunkte = kalibrierung?.passpunkte ?? [];
    const achsen = kalibrierung?.achsen ?? [];
    const nurAnsicht = !onChange;

    const [modus, setModus] = useState<Modus>('passpunkt');
    const [zoom, setZoom] = useState<number>(1);
    const [entwurf, setEntwurf] = useState<PlanCoordinates | null>(null);
    const [name, setName] = useState('');
    const [utmText, setUtmText] = useState('');
    const [utmFehler, setUtmFehler] = useState<string | null>(null);
    const [messung, setMessung] = useState<Messung | null>(null);
    const [jetzt, setJetzt] = useState(0);
    const [meldung, setMeldung] = useState<Meldung | null>(null);
    const [quellPlanId, setQuellPlanId] = useState('');
    const [bildFehler, setBildFehler] = useState(false);
    const [gpsMoeglich] = useState<boolean>(standortVerfuegbar);
    // Immer abschaltbar (Codex-Review 27.09., P1): GPS-Messung nur mit Gerät UND Projekt an.
    const [geraet] = useVerortungGeraet();
    const gpsErlaubt = gpsMoeglich && geraet.aktiv && verortung.aktiv;

    const sensoren = useStandortSensoren(messung !== null && gpsErlaubt);
    const timer = useRef<number | null>(null);
    const flaecheRef = useRef<HTMLDivElement>(null);

    const aktuell = useRef<Aktuell>({ positionen: [], kalibrierung: undefined, onChange: undefined, gpsErlaubt: false });
    useEffect(() => {
        aktuell.current = { positionen: sensoren.positionen, kalibrierung, onChange, gpsErlaubt };
    });

    // Uhr nur während der Messung (Fortschritt), nicht im Render.
    useEffect(() => {
        if (messung === null) return undefined;
        const tick = () => setJetzt(Date.now());
        const erster = window.setTimeout(tick, 0);
        const takt = window.setInterval(tick, 500);
        return () => { window.clearTimeout(erster); window.clearInterval(takt); };
    }, [messung]);

    const timerLoeschen = () => {
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = null;
    };
    useEffect(() => () => {
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = null;
    }, []);

    const georeferenz = useMemo(() => planGeoreferenz(kalibrierung), [kalibrierung]);

    /** Andere Pläne mit gültiger Georeferenz — Quellen für die Achsen-Übertragung. */
    const quellen = useMemo(() => plaene
        .filter((p) => p.id !== plan.id)
        .map((p) => ({ plan: p, k: verortung.plaene?.[p.id], geo: planGeoreferenz(verortung.plaene?.[p.id]) }))
        .flatMap((q) => (q.k && istGeoreferenz(q.geo) ? [{ plan: q.plan, k: q.k, geo: q.geo }] : [])),
    [plaene, plan.id, verortung.plaene]);
    const quelle = quellen.find((q) => q.plan.id === quellPlanId) ?? quellen[0] ?? null;

    /** Achsnamen anderer Pläne, die hier noch fehlen — zum schnellen Übernehmen des Namens. */
    const achsVorschlaege = useMemo(() => {
        const hier = new Set((verortung.plaene?.[plan.id]?.achsen ?? []).map((a) => geschossNameNormalisieren(a.name)));
        const namen = new Map<string, string>();
        for (const p of plaene) {
            if (p.id === plan.id) continue;
            for (const a of verortung.plaene?.[p.id]?.achsen ?? []) {
                const s = geschossNameNormalisieren(a.name);
                if (s && !hier.has(s) && !namen.has(s)) namen.set(s, a.name);
            }
        }
        return [...namen.values()].slice(0, 12);
    }, [plaene, plan.id, verortung.plaene]);

    const entwurfVerwerfen = () => {
        setEntwurf(null);
        setName('');
        setUtmText('');
        setUtmFehler(null);
    };

    const messungAbbrechen = () => {
        timerLoeschen();
        setMessung(null);
    };

    const schliessen = () => {
        timerLoeschen();
        setMessung(null);
        onSchliessen();
    };

    // Android-Zurück: erst Messung abbrechen, dann Entwurf verwerfen, dann schließen.
    const zurueckTaste = useRef<() => void>(() => undefined);
    useEffect(() => {
        zurueckTaste.current = () => {
            if (messung) messungAbbrechen();
            else if (entwurf) entwurfVerwerfen();
            else schliessen();
        };
    });
    useEffect(() => registriereZurueck(() => zurueckTaste.current(), () => flaecheRef.current), []);

    const tippen = (e: React.MouseEvent<HTMLDivElement>) => {
        if (nurAnsicht || messung) return;
        const p = punktAusKlick(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY);
        if (!p) return;
        setEntwurf(p);
        setUtmFehler(null);
        setMeldung(null);
    };

    const bildGeladen = (e: React.SyntheticEvent<HTMLImageElement>) => {
        setBildFehler(false);
        if (!onChange) return;
        const img = e.currentTarget;
        const neu = seitenverhaeltnisNeu(kalibrierung?.seitenverhaeltnis, img.naturalWidth, img.naturalHeight);
        if (neu !== null) onChange({ ...(kalibrierung ?? {}), seitenverhaeltnis: neu });
    };

    const utmSpeichern = () => {
        if (!entwurf || !onChange) return;
        const u = utmFuerPlanLesen(utmText, kalibrierung);
        if ('fehler' in u) { setUtmFehler(u.fehler); return; }
        const punktName = name.trim() || standardPasspunktName(kalibrierung);
        onChange(passpunktHinzufuegen(kalibrierung, {
            id: crypto.randomUUID(), name: punktName, plan: entwurf, ost: u.ost, nord: u.nord, quelle: 'utm', genauigkeitM: null,
        }, u.zone));
        entwurfVerwerfen();
        setMeldung({ text: `Passpunkt „${punktName}“ gespeichert (${utmFormatieren(u, 2)}).`, ton: 'ok' });
    };

    const gpsAbschliessen = (m: Messung) => {
        timer.current = null;
        setMessung(null);
        const a = aktuell.current;
        if (!a.gpsErlaubt) {
            setMeldung({ text: 'GPS-Messung abgebrochen: Sensor-Verortung wurde auf dem Gerät oder im Projekt abgeschaltet.', ton: 'fehler' });
            return;
        }
        const vorhandeneZone = zoneFuerPlan(a.kalibrierung);
        const mittel = gpsMitteln(gpsFixeAb(a.positionen, m.start), vorhandeneZone !== null ? { zone: vorhandeneZone } : undefined);
        if (!mittel) {
            setMeldung({
                text: 'Kein verwertbarer GPS-Fix in 30 s — unter einer Decke? GPS aus? Freie Sicht zum Himmel suchen und erneut messen, oder UTM eintragen.',
                ton: 'fehler',
            });
            return;
        }
        if (!a.onChange) return;
        const zone = vorhandeneZone ?? utmZoneFuer(mittel.lng);
        const u = wgs84ZuUtm(mittel.lat, mittel.lng, zone);
        const punktName = m.name || standardPasspunktName(a.kalibrierung);
        a.onChange(passpunktHinzufuegen(a.kalibrierung, {
            id: crypto.randomUUID(), name: punktName, plan: m.punkt, ost: u.ost, nord: u.nord, quelle: 'gps', genauigkeitM: mittel.genauigkeitM,
        }, zone));
        setEntwurf(null);
        setName('');
        setUtmText('');
        setUtmFehler(null);
        setMeldung({ text: `Passpunkt „${punktName}“ gemessen: ±${zahlText(mittel.genauigkeitM, 1)} m aus ${mittel.anzahl} Fixen.`, ton: 'ok' });
    };

    const gpsStarten = () => {
        if (!entwurf || !onChange || messung || !gpsErlaubt) return;
        const m: Messung = { start: Date.now(), punkt: entwurf, name: name.trim() };
        setMessung(m);
        setMeldung(null);
        setUtmFehler(null);
        timerLoeschen();
        timer.current = window.setTimeout(() => gpsAbschliessen(m), GPS_DAUER_MS);
    };

    const achseSpeichern = () => {
        if (!entwurf || !onChange) return;
        const achsName = name.trim();
        if (!achsName) { setMeldung({ text: 'Name der Achse fehlt — z. B. „A/1“.', ton: 'fehler' }); return; }
        const verschoben = achsen.some((a) => geschossNameNormalisieren(a.name) === geschossNameNormalisieren(achsName));
        onChange(achseSetzen(kalibrierung, { name: achsName, plan: entwurf }));
        entwurfVerwerfen();
        setMeldung({ text: verschoben ? `Achse „${achsName}“ verschoben.` : `Achse „${achsName}“ gespeichert.`, ton: 'ok' });
    };

    const vonPlanUebernehmen = () => {
        if (!onChange || !quelle) return;
        const r = achsenUebertragen(kalibrierung, quelle.k, quelle.geo, quelle.plan.name);
        if ('fehler' in r) { setMeldung({ text: r.fehler, ton: 'fehler' }); return; }
        onChange(r.plan);
        setMeldung({ text: `${r.anzahl} Passpunkte aus gemeinsamen Achsen von „${quelle.plan.name}“ übertragen.`, ton: 'ok' });
    };

    const gpsZustand = gpsZustandAnzeige(sensoren.status, sensoren.pruefung);
    const fixeSeitStart = messung ? sensoren.positionen.filter((p) => p.empfangen >= messung.start) : [];
    const letzterFix = fixeSeitStart.length ? fixeSeitStart[fixeSeitStart.length - 1] : null;
    const vergangenMs = messung ? Math.min(GPS_DAUER_MS, Math.max(0, jetzt - messung.start)) : 0;
    const restS = Math.ceil((GPS_DAUER_MS - vergangenMs) / 1000);
    const zoneAnzeige = kalibrierung?.zone ?? 32;
    const zoomIndex = ZOOM_STUFEN.findIndex((z) => z === zoom);

    return (
        <div
            ref={flaecheRef}
            className="fixed inset-0 z-[120] bg-slate-900 flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label={`Plan kalibrieren: ${plan.name}`}
        >
            {/* ─── Kopf ─────────────────────────────────────────────────── */}
            <div className="shrink-0 bg-slate-800 text-white px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Plan kalibrieren</p>
                    <h3 className="font-bold truncate">{plan.name}</h3>
                </div>
                <button
                    type="button"
                    onClick={schliessen}
                    aria-label="Schließen"
                    title="Schließen"
                    className="shrink-0 rounded-lg p-2 text-slate-200 hover:bg-slate-700 active:bg-slate-600"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            {/* ─── Werkzeug ─────────────────────────────────────────────── */}
            <div className="shrink-0 bg-slate-800 border-t border-slate-700 px-3 py-2 flex flex-wrap items-center gap-2">
                {nurAnsicht ? (
                    <span className="text-xs font-semibold text-amber-300">Nur Ansicht</span>
                ) : (
                    <div className="flex rounded-lg bg-slate-700 p-0.5" role="group" aria-label="Was wird angetippt?">
                        {(['passpunkt', 'achse'] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => setModus(m)}
                                disabled={messung !== null}
                                aria-pressed={modus === m}
                                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${modus === m ? (m === 'passpunkt' ? 'bg-rose-600 text-white' : 'bg-blue-600 text-white') : 'text-slate-200'}`}
                            >
                                {m === 'passpunkt' ? 'Passpunkt' : 'Achse'}
                            </button>
                        ))}
                    </div>
                )}
                <div className="flex items-center gap-1 ml-auto">
                    <button
                        type="button"
                        onClick={() => setZoom(ZOOM_STUFEN[Math.max(0, zoomIndex - 1)])}
                        disabled={zoomIndex <= 0}
                        aria-label="Verkleinern"
                        className="rounded-md p-1.5 text-slate-200 disabled:text-slate-500 hover:bg-slate-700"
                    >
                        <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-slate-300 w-10 text-center">{Math.round(zoom * 100)} %</span>
                    <button
                        type="button"
                        onClick={() => setZoom(ZOOM_STUFEN[Math.min(ZOOM_STUFEN.length - 1, zoomIndex + 1)])}
                        disabled={zoomIndex >= ZOOM_STUFEN.length - 1}
                        aria-label="Vergrößern"
                        className="rounded-md p-1.5 text-slate-200 disabled:text-slate-500 hover:bg-slate-700"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
                {!nurAnsicht && (
                    <p className="w-full text-[11px] text-slate-400">
                        {modus === 'passpunkt'
                            ? 'Stelle im Plan antippen, deren Lage Sie kennen (Absteckpunkt, Gebäudeecke) — dann UTM eintragen oder vor Ort GPS messen.'
                            : 'Achskreuz antippen und benennen (z. B. „A/1“) — gleichnamige Achsen verbinden die Pläne.'}
                    </p>
                )}
            </div>

            {meldung && (
                <div
                    role="status"
                    className={`shrink-0 px-4 py-2 text-sm flex items-start gap-2 ${meldung.ton === 'ok' ? 'bg-emerald-700 text-white' : 'bg-rose-700 text-white'}`}
                >
                    <span className="flex-1">{meldung.text}</span>
                    <button type="button" onClick={() => setMeldung(null)} aria-label="Meldung schließen" className="shrink-0">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* ─── Plan + Listen ────────────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="overflow-x-auto p-2 sm:p-4">
                    <div
                        className={`relative mx-auto bg-black rounded border-2 border-slate-600 ${nurAnsicht || messung ? '' : 'cursor-crosshair'}`}
                        style={{ width: `${zoom * 100}%` }}
                        onClick={tippen}
                    >
                        <img
                            src={plan.image}
                            alt={plan.name}
                            className="block w-full h-auto select-none"
                            draggable={false}
                            onLoad={bildGeladen}
                            onError={() => setBildFehler(true)}
                        />
                        {achsen.map((a) => (
                            <Marker key={`a-${a.name}`} punkt={a.plan} farbe="bg-blue-600" name={a.name} />
                        ))}
                        {passpunkte.map((p) => (
                            <Marker key={`p-${p.id}`} punkt={p.plan} farbe="bg-rose-600" name={p.name} />
                        ))}
                        {messung && <Marker punkt={messung.punkt} farbe="bg-amber-500" name={messung.name || 'GPS …'} pulsieren />}
                        {!messung && entwurf && (
                            <Marker punkt={entwurf} farbe="bg-amber-500" name={name.trim() || 'neu'} pulsieren />
                        )}
                    </div>
                    {bildFehler && (
                        <p className="mt-2 text-sm text-rose-300">Planbild konnte nicht geladen werden — Verbindung prüfen.</p>
                    )}
                </div>

                <div className="bg-white rounded-t-2xl p-4 space-y-4">
                    <div>
                        <p className="text-sm font-semibold text-slate-800 mb-1">Georeferenz</p>
                        <GeoreferenzStatus ergebnis={georeferenz} passpunkte={passpunkte.length} />
                    </div>

                    <div>
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-600" /> Passpunkte ({passpunkte.length})
                        </p>
                        {passpunkte.length === 0 ? (
                            <p className="text-xs text-slate-500 mt-1">
                                Noch keine. Mindestens 2, besser 3 Punkte mit ≥ 20 m Abstand — ab 3 Punkten gibt es einen Restfehler.
                            </p>
                        ) : (
                            <ul className="mt-1 divide-y divide-slate-100">
                                {passpunkte.map((p) => (
                                    <li key={p.id} className="py-1.5 flex items-center gap-2 text-xs text-slate-700">
                                        <span className="min-w-0 flex-1">
                                            <strong className="text-slate-900">{p.name || '(ohne Name)'}</strong>{' '}
                                            <span className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-600">{QUELLE_TEXT[p.quelle]}</span>
                                            <span className="block font-mono text-slate-500 truncate">
                                                {utmFormatieren({ zone: zoneAnzeige, ost: p.ost, nord: p.nord }, 2)}
                                                {typeof p.genauigkeitM === 'number' && ` · ±${zahlText(p.genauigkeitM, 1)} m`}
                                            </span>
                                        </span>
                                        {onChange && (
                                            <button
                                                type="button"
                                                onClick={() => onChange(passpunktLoeschen(kalibrierung, p.id))}
                                                aria-label={`Passpunkt ${p.name} löschen`}
                                                title="Löschen"
                                                className="shrink-0 rounded-lg p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div>
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600" /> Achsen ({achsen.length})
                        </p>
                        {achsen.length === 0 ? (
                            <p className="text-xs text-slate-500 mt-1">
                                Keine. Zwei gleich benannte Achskreuze (z. B. „A/1“, „F/9“) auf diesem und einem georeferenzierten Plan
                                genügen, um die Georeferenz ohne GPS-Gang zu übernehmen.
                            </p>
                        ) : (
                            <ul className="mt-1 divide-y divide-slate-100">
                                {achsen.map((a) => (
                                    <li key={a.name} className="py-1.5 flex items-center gap-2 text-xs text-slate-700">
                                        <span className="min-w-0 flex-1">
                                            <strong className="text-slate-900">{a.name}</strong>
                                            <span className="text-slate-400"> · x {zahlText(a.plan.x, 1)} % · y {zahlText(a.plan.y, 1)} %</span>
                                        </span>
                                        {onChange && (
                                            <button
                                                type="button"
                                                onClick={() => onChange(achseLoeschen(kalibrierung, a.name))}
                                                aria-label={`Achse ${a.name} löschen`}
                                                title="Löschen"
                                                className="shrink-0 rounded-lg p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {onChange && (
                        <div className="rounded-xl border border-slate-200 p-3">
                            <p className="text-sm font-semibold text-slate-800">Von anderem Plan übernehmen</p>
                            {quellen.length === 0 ? (
                                <p className="text-xs text-slate-500 mt-1">
                                    Kein anderer Plan ist georeferenziert. Erst einen Lageplan mit Passpunkten kalibrieren.
                                </p>
                            ) : (
                                <>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Überträgt die Lage über gleichnamige Achsen (mind. 2). Ersetzt die bisher übertragenen
                                        Passpunkte; GPS- und UTM-Punkte bleiben. Nach Änderung der Achsen erneut übernehmen.
                                    </p>
                                    <div className="mt-2 flex flex-col sm:flex-row gap-2">
                                        <select
                                            value={quelle?.plan.id ?? ''}
                                            onChange={(e) => setQuellPlanId(e.target.value)}
                                            aria-label="Quellplan"
                                            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                                        >
                                            {quellen.map((q) => (
                                                <option key={q.plan.id} value={q.plan.id}>
                                                    {q.plan.name} ({q.k.achsen?.length ?? 0} Achse{(q.k.achsen?.length ?? 0) === 1 ? '' : 'n'})
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={vonPlanUebernehmen}
                                            disabled={messung !== null}
                                            className="rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2"
                                        >
                                            Übernehmen
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Neuer Punkt ──────────────────────────────────────────── */}
            {onChange && (entwurf || messung) && (
                <div className="shrink-0 bg-white border-t-2 border-amber-400 p-3 space-y-2 max-h-[55vh] overflow-y-auto shadow-[0_-8px_24px_rgba(0,0,0,0.25)]">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                            {modus === 'passpunkt' ? <MapPin className="w-4 h-4 text-rose-600" /> : <Crosshair className="w-4 h-4 text-blue-600" />}
                            {modus === 'passpunkt' ? 'Neuer Passpunkt' : 'Neue Achse'}
                        </p>
                        {!messung && (
                            <button type="button" onClick={entwurfVerwerfen} className="text-xs text-slate-600 underline">
                                Verwerfen
                            </button>
                        )}
                    </div>
                    {entwurf && !messung && (
                        <p className="text-[11px] text-slate-500">
                            Stelle x {zahlText(entwurf.x, 1)} % · y {zahlText(entwurf.y, 1)} % — erneut tippen verschiebt sie.
                        </p>
                    )}
                    <input
                        type="text"
                        value={messung ? messung.name : name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={messung !== null}
                        placeholder={modus === 'passpunkt' ? `Name, z. B. „Ecke Nord“ (leer: ${standardPasspunktName(kalibrierung)})` : 'Name, z. B. „A/1“'}
                        aria-label={modus === 'passpunkt' ? 'Name des Passpunkts' : 'Name der Achse'}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />

                    {modus === 'achse' ? (
                        <>
                            {achsVorschlaege.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {achsVorschlaege.map((v) => (
                                        <button
                                            key={v}
                                            type="button"
                                            onClick={() => setName(v)}
                                            className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800"
                                        >
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={achseSpeichern}
                                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold px-4 py-3"
                            >
                                Achse speichern
                            </button>
                        </>
                    ) : (
                        <>
                            {!messung && (
                                <>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="text"
                                            value={utmText}
                                            onChange={(e) => { setUtmText(e.target.value); setUtmFehler(null); }}
                                            placeholder={`UTM, z. B. ${kalibrierung?.zone ?? 32} 565123,45 5934567,89`}
                                            aria-label="UTM-Koordinate"
                                            aria-invalid={utmFehler !== null}
                                            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-mono ${utmFehler ? 'border-rose-400' : 'border-slate-300'}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={utmSpeichern}
                                            disabled={!utmText.trim()}
                                            className="rounded-lg bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2"
                                        >
                                            Mit UTM speichern
                                        </button>
                                    </div>
                                    {utmFehler && <p className="text-xs text-rose-700">{utmFehler}</p>}
                                    {zoneFuerPlan(kalibrierung) !== null && (
                                        <p className="text-[11px] text-slate-500">Zone {zoneFuerPlan(kalibrierung)} gilt (erster Passpunkt).</p>
                                    )}
                                </>
                            )}
                            {gpsMoeglich && !gpsErlaubt && !messung && (
                                <p className="text-[11px] text-slate-500">
                                    GPS-Messung aus: Sensor-Verortung ist auf diesem Gerät (App-Info) oder im Projekt abgeschaltet — UTM eintragen oder einschalten.
                                </p>
                            )}
                            {gpsErlaubt && !messung && (
                                <button
                                    type="button"
                                    onClick={gpsStarten}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-sky-600 text-sky-700 bg-white active:bg-sky-50 text-sm font-semibold px-4 py-3"
                                >
                                    <Crosshair className="w-4 h-4" /> oder vor Ort: GPS 30 s messen
                                </button>
                            )}
                            {messung && (
                                <div className="space-y-2">
                                    <p className="text-xs text-slate-600">Auf dem Punkt stehen bleiben und das Gerät ruhig halten.</p>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-3 rounded-full bg-slate-200 overflow-hidden">
                                            <div className="h-full bg-sky-600 transition-all" style={{ width: `${(vergangenMs / GPS_DAUER_MS) * 100}%` }} />
                                        </div>
                                        <span className="text-sm font-semibold text-slate-700 w-12 text-right">{restS} s</span>
                                        <button type="button" onClick={messungAbbrechen} className="text-xs text-slate-600 underline">
                                            Abbrechen
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        {letzterFix
                                            ? `${fixeSeitStart.length} Fixe · zuletzt ±${zahlText(letzterFix.genauigkeitM, 1)} m`
                                            : 'Warte auf GPS-Fix … (freie Sicht zum Himmel)'}
                                    </p>
                                    {gpsZustand === 'keine_erlaubnis' && (
                                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
                                            Genauer Standort ist nicht freigegeben.
                                            <button
                                                type="button"
                                                onClick={() => { void sensoren.erlaubnisAnfragen(); }}
                                                className="ml-2 rounded-md bg-sky-600 text-white font-semibold px-2 py-1"
                                            >
                                                Genauen Standort erlauben
                                            </button>
                                        </div>
                                    )}
                                    {gpsZustand === 'aus' && (
                                        <p className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
                                            GPS ist aus — in den Android-Schnelleinstellungen „Standort“ einschalten.
                                        </p>
                                    )}
                                    {gpsZustand === 'nicht_vorhanden' && (
                                        <p className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
                                            Kein GPS in diesem Gerät — bitte UTM eintragen.
                                        </p>
                                    )}
                                    {sensoren.fehler && <p className="text-xs text-rose-700">{sensoren.fehler}</p>}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
